import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { restaurantsService } from '../../services/restaurantsService';
import { settingsService } from '../../services/settingsService';
import { RestaurantDetailsResponse, ApplicationStatus, RestaurantCommissionConfig, CommissionType } from '../../types/restaurants';
import { AdminType } from '../../types/auth';
import ChangeRestaurantStatusModal from '../modals/ChangeRestaurantStatusModal';
import ChangeRestaurantBalanceModal from '../modals/ChangeRestaurantBalanceModal';
import RestaurantCommissionModal from '../modals/RestaurantCommissionModal';
import ImageViewerModal from '../modals/ImageViewerModal';
import EditRestaurantModal from '../modals/EditRestaurantModal';
import EditFoodModal from '../modals/EditFoodModal';
import LocationMap from '../map/LocationMap';
import { imageIdToUrl } from '../../utils/imageUtils';
import { useAuth } from '../../contexts/AuthContext';
import RestaurantStatisticsModal from '../modals/RestaurantStatisticsModal';
import RestaurantTransactions from '../restaurant/RestaurantTransactions';
import ManageRestaurantCategoriesModal from '../modals/ManageRestaurantCategoriesModal';
import { RestaurantFoodResponse } from '../../types/restaurants';
import './Page.css';

const RestaurantDetails: React.FC = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentAdmin } = useAuth();
  const isSuperAdmin = currentAdmin?.adminType === AdminType.SUPER_ADMIN;

  const [details, setDetails] = useState<RestaurantDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Modal states
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [isEditRestaurantModalOpen, setIsEditRestaurantModalOpen] = useState(false);
  const [isEditFoodModalOpen, setIsEditFoodModalOpen] = useState(false);
  const [isManageCategoriesModalOpen, setIsManageCategoriesModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [commissionDeleting, setCommissionDeleting] = useState(false);

  // Image viewer state
  const [viewerImage, setViewerImage] = useState<{ url: string; title: string } | null>(null);

  // Statistics modal state
  const [isStatisticsModalOpen, setIsStatisticsModalOpen] = useState(false);

  // Food editing state
  const [selectedFood, setSelectedFood] = useState<(RestaurantFoodResponse & { categoryId: number }) | null>(null);

  // Commission state
  const [commissionConfig, setCommissionConfig] = useState<RestaurantCommissionConfig | null>(null);
  const [globalDefaultCommission, setGlobalDefaultCommission] = useState<number>(0);

  useEffect(() => {
    if (restaurantId) {
      fetchRestaurantDetails(parseInt(restaurantId));
      fetchCommissionConfig(parseInt(restaurantId));
    }
    fetchGlobalSettings();
  }, [restaurantId]);

  const fetchRestaurantDetails = async (id: number) => {
    setLoading(true);
    setError('');

    try {
      const response = await restaurantsService.getRestaurantDetails(id);

      if (response.success && response.data) {
        setDetails(response.data);
      } else {
        setError(response.error || t('restaurantDetails.failedToFetch'));
      }
    } catch (error) {
      setError(t('restaurantDetails.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = async (newStatus: ApplicationStatus, blockReason: string | null) => {
    if (!restaurantId) return;

    setModalLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await restaurantsService.changeApplicationStatus({
        restaurantId: parseInt(restaurantId),
        applicationStatus: newStatus,
        blockReason: blockReason
      });

      if (response.success) {
        setSuccessMessage(t('restaurantDetails.statusUpdatedSuccess'));
        setIsStatusModalOpen(false);
        // Refresh the details
        await fetchRestaurantDetails(parseInt(restaurantId));
      } else {
        setError(response.error || t('restaurantDetails.failedToChangeStatus'));
      }
    } catch (error) {
      setError(t('restaurantDetails.networkError'));
    } finally {
      setModalLoading(false);
    }
  };

  const handleChangeBalance = async (newBalance: number) => {
    if (!restaurantId) return;

    setModalLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await restaurantsService.changeBalance({
        restaurantId: parseInt(restaurantId),
        balance: newBalance
      });

      if (response.success) {
        setSuccessMessage(t('restaurantDetails.balanceUpdatedSuccess'));
        setIsBalanceModalOpen(false);
        // Refresh the details
        await fetchRestaurantDetails(parseInt(restaurantId));
      } else {
        setError(response.error || t('restaurantDetails.failedToChangeBalance'));
      }
    } catch (error) {
      setError(t('restaurantDetails.networkError'));
    } finally {
      setModalLoading(false);
    }
  };

  const fetchGlobalSettings = async () => {
    try {
      const response = await settingsService.getSettings();
      if (response.success && response.data) {
        setGlobalDefaultCommission(response.data.restaurantPerOrderChargePercentage);
      }
    } catch (error) {
      // Silent fail - not critical
      console.error('Failed to fetch global settings:', error);
    }
  };

  const fetchCommissionConfig = async (id: number) => {
    try {
      const response = await restaurantsService.getRestaurantCommission(id);
      if (response.success && response.data) {
        setCommissionConfig(response.data);
      } else {
        // No custom config - using global default
        setCommissionConfig(null);
      }
    } catch (error) {
      // No custom config or error - using global default
      setCommissionConfig(null);
    }
  };

  const handleConfigureCommission = async (config: {
    commissionType: CommissionType;
    fixedPercentage?: number;
    tiers?: { orderThreshold: number; percentage: number }[];
  }) => {
    if (!restaurantId) return;

    setModalLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await restaurantsService.setRestaurantCommission({
        restaurantId: parseInt(restaurantId),
        ...config
      });

      if (response.success) {
        setSuccessMessage(t('restaurantDetails.commissionUpdatedSuccess'));
        setIsCommissionModalOpen(false);
        // Refresh the commission config
        await fetchCommissionConfig(parseInt(restaurantId));
      } else {
        setError(response.error || t('restaurantDetails.failedToUpdateCommission'));
      }
    } catch (error) {
      setError(t('restaurantDetails.networkError'));
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteCommission = async () => {
    if (!restaurantId) return;

    setCommissionDeleting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await restaurantsService.deleteRestaurantCommission(parseInt(restaurantId));

      if (response.success) {
        setSuccessMessage(t('restaurantDetails.commissionDeletedSuccess'));
        setIsCommissionModalOpen(false);
        // Clear the commission config
        setCommissionConfig(null);
      } else {
        setError(response.error || t('restaurantDetails.failedToDeleteCommission'));
      }
    } catch (error) {
      setError(t('restaurantDetails.networkError'));
    } finally {
      setCommissionDeleting(false);
    }
  };

  const handleEditRestaurant = async (data: {
    name?: string;
    description?: string;
    isOpen?: boolean;
    workStartTime?: string;
    workEndTime?: string;
    bannerId?: string;
  }) => {
    if (!restaurantId) return;

    setModalLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await restaurantsService.updateRestaurant(parseInt(restaurantId), data);

      if (response.success) {
        setSuccessMessage(t('restaurantDetails.restaurantUpdatedSuccess'));
        setIsEditRestaurantModalOpen(false);
        // Refresh the details
        await fetchRestaurantDetails(parseInt(restaurantId));
      } else {
        setError(response.error || t('restaurantDetails.failedToUpdateRestaurant'));
      }
    } catch (error) {
      setError(t('restaurantDetails.networkError'));
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditFood = async (data: {
    foodId: number;
    categoryId?: number;
    name?: string;
    description?: string;
    price?: number;
    isAvailable?: boolean;
    foodImageIds?: string[];
  }) => {
    if (!restaurantId) return;

    setModalLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await restaurantsService.updateFood(parseInt(restaurantId), data);

      if (response.success) {
        setSuccessMessage(t('restaurantDetails.foodUpdatedSuccess'));
        setIsEditFoodModalOpen(false);
        setSelectedFood(null);
        // Refresh the details
        await fetchRestaurantDetails(parseInt(restaurantId));
      } else {
        setError(response.error || t('restaurantDetails.failedToUpdateFood'));
      }
    } catch (error) {
      setError(t('restaurantDetails.networkError'));
    } finally {
      setModalLoading(false);
    }
  };

  const openEditFoodModal = (food: RestaurantFoodResponse, categoryId: number) => {
    setSelectedFood({ ...food, categoryId });
    setIsEditFoodModalOpen(true);
  };

  const formatPrice = (price: number): string => {
    return `${price.toLocaleString()} soum`;
  };

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(
          <span key={i} style={{ color: '#FFD700', fontSize: '1.2rem' }}>★</span>
        );
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(
          <span key={i} style={{ color: '#FFD700', fontSize: '1.2rem' }}>☆</span>
        );
      } else {
        stars.push(
          <span key={i} style={{ color: '#ddd', fontSize: '1.2rem' }}>★</span>
        );
      }
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div style={{ display: 'flex', gap: '2px' }}>{stars}</div>
        <span style={{ fontSize: '1rem', fontWeight: 'bold', color: '#333', marginLeft: '4px' }}>
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  const getCommissionDisplay = (): string => {
    if (!commissionConfig) {
      return t('restaurantDetails.globalDefault', { percentage: globalDefaultCommission });
    }

    if (commissionConfig.commissionType === CommissionType.FIXED) {
      return t('restaurantDetails.commissionFixed', { percentage: commissionConfig.fixedPercentage });
    }

    if (commissionConfig.commissionType === CommissionType.TIERED) {
      return t('restaurantDetails.commissionTiered', { count: commissionConfig.tiers.length });
    }

    return t('restaurantDetails.globalDefault', { percentage: globalDefaultCommission });
  };

  const getStatusColor = (status: ApplicationStatus): string => {
    switch (status) {
      case ApplicationStatus.PENDING:
        return '#ffc107';
      case ApplicationStatus.ACCEPTED:
        return '#28a745';
      case ApplicationStatus.BLOCKED:
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getStatusDisplay = (status: ApplicationStatus): string => {
    switch (status) {
      case ApplicationStatus.PENDING:
        return t('restaurantDetails.pending');
      case ApplicationStatus.ACCEPTED:
        return t('restaurantDetails.accepted');
      case ApplicationStatus.BLOCKED:
        return t('restaurantDetails.blocked');
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="loading-container">{t('restaurantDetails.loadingRestaurantDetails')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="error-message">{error}</div>
          <button onClick={() => navigate('/restaurants')} className="back-button">
            {t('restaurantDetails.backToRestaurants')}
          </button>
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="no-data">{t('restaurantDetails.restaurantNotFound')}</div>
          <button onClick={() => navigate('/restaurants')} className="back-button">
            {t('restaurantDetails.backToRestaurants')}
          </button>
        </div>
      </div>
    );
  }

  const { restaurant, branches, categoriesWithFoods } = details;

  return (
    <div className="page">
      <div className="page-header">
        <div className="header-with-back">
          <button onClick={() => navigate('/restaurants')} className="back-button">
            ← {t('restaurantDetails.backToRestaurants')}
          </button>
          <div>
            <h1>{t('restaurantDetails.title', { restaurantName: restaurant.name })}</h1>
            <p>{t('restaurantDetails.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* Restaurant Info */}
        <div className="order-details-grid">
          <div className="detail-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>{t('restaurantDetails.basicInformation')}</h3>
              <button
                className="action-button accept"
                onClick={() => setIsEditRestaurantModalOpen(true)}
                style={{
                  backgroundColor: '#007bff',
                  borderColor: '#007bff',
                  padding: '0.4rem 0.8rem',
                  fontSize: '0.85rem'
                }}
              >
                {t('restaurantDetails.editRestaurant')}
              </button>
            </div>
            <div className="info-grid">
              <div>
                <strong>{t('restaurantDetails.id')}:</strong> {restaurant.id}
              </div>
              <div>
                <strong>{t('restaurantDetails.phone')}:</strong> {restaurant.phone}
              </div>
              <div>
                <strong>{t('restaurantDetails.workingHours')}:</strong> {restaurant.workStartTime} - {restaurant.workEndTime}
              </div>
              <div>
                <strong>{t('restaurantDetails.status')}:</strong>{' '}
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(restaurant.applicationStatus) }}
                >
                  {getStatusDisplay(restaurant.applicationStatus)}
                </span>
              </div>
              <div>
                <strong>{t('restaurantDetails.openNow')}:</strong> {restaurant.isOpen ? `🟢 ${t('restaurantDetails.yes')}` : `🔴 ${t('restaurantDetails.no')}`}
              </div>
              <div>
                <strong>{t('restaurantDetails.rating')}:</strong> {renderStars(restaurant.rating)}
              </div>
                <div>
                    <strong>{t('restaurantDetails.description')}:</strong>  {restaurant.description}
                </div>
            </div>
          </div>

          <div className="detail-card">
            <h3>{t('restaurantDetails.financial')}</h3>
            <div className="info-grid">
              <div>
                <strong>{t('restaurantDetails.balance')}:</strong> {formatPrice(restaurant.balance)}
              </div>
              <div>
                <strong>{t('restaurantDetails.commission')}:</strong> {getCommissionDisplay()}
              </div>
            </div>
          </div>
        </div>

        {/* Restaurant Transactions */}
        {isSuperAdmin && (
          <RestaurantTransactions
            restaurantId={restaurant.id}
            restaurantName={restaurant.name}
            onTransactionCreated={() => fetchRestaurantDetails(restaurant.id)}
          />
        )}

        {/* Banner Image */}
        <div className="detail-card full-width">
          <h3>{t('restaurantDetails.restaurantBanner')}</h3>
          <div
            style={{
              border: '1px solid #ddd',
              borderRadius: '4px',
              overflow: 'hidden',
              backgroundColor: '#f8f9fa',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              maxWidth: '100%',
              width: '100%',
              position: 'relative',
              paddingBottom: '25%',
              height: 0
            }}
            onClick={() => setViewerImage({
              url: imageIdToUrl(restaurant.bannerImageId) || '',
              title: `${restaurant.name} - Banner`
            })}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.01)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <img
              src={imageIdToUrl(restaurant.bannerImageId) || ''}
              alt="Restaurant Banner"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = `<div style="padding: 0.75rem; text-align: center; color: #666;">${t('restaurantDetails.bannerNotAvailable')}</div>`;
              }}
            />
          </div>
        </div>

        {/* Block Reason if blocked */}
        {restaurant.applicationStatus === ApplicationStatus.BLOCKED && restaurant.applicationStatusBlockReason && (
          <div className="detail-card full-width" style={{ borderLeft: '4px solid #dc3545' }}>
            <h3>{t('restaurantDetails.blockReason')}</h3>
            <p style={{ color: '#dc3545' }}>{restaurant.applicationStatusBlockReason}</p>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="success-message" style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #c3e6cb'
          }}>
            {successMessage}
          </div>
        )}

        {/* Admin Actions */}
        <div className="action-buttons">
          <h3>{t('restaurantDetails.adminActions')}</h3>
          <div className="action-buttons-grid">
            <button
              className="action-button accept"
              onClick={() => setIsStatusModalOpen(true)}
            >
              {t('restaurantDetails.changeStatus')}
            </button>
            {
                isSuperAdmin && (
                  <>
                    { /*
                        <button
                      className="action-button change-branch"
                      onClick={() => setIsBalanceModalOpen(true)}
                    >
                      {t('restaurantDetails.changeBalance')}
                    </button>
                    */
                    }
                    <button
                      className="action-button accept"
                      onClick={() => setIsCommissionModalOpen(true)}
                      style={{ backgroundColor: '#fd7e14', borderColor: '#fd7e14' }}
                    >
                      {t('restaurantDetails.configureCommission')}
                    </button>
                  </>
                )

            }
            <button
              className="action-button accept"
              onClick={() => setIsStatisticsModalOpen(true)}
              style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8' }}
            >
              {t('statistics.restaurantStatistics')}
            </button>
            {isSuperAdmin && (
              <button
                className="action-button accept"
                onClick={() => setIsManageCategoriesModalOpen(true)}
                style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1' }}
              >
                {t('restaurantDetails.manageCategories')}
              </button>
            )}
          </div>
        </div>

        {/* Branches */}
        <div className="detail-card full-width">
          <h3>{t('restaurantDetails.branches', { count: branches.length })}</h3>
          {branches.length === 0 ? (
            <div className="no-data">{t('restaurantDetails.noBranches')}</div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '1rem'
            }}>
              {branches.map((branch) => (
                <div key={branch.id} style={{
                  padding: '0.75rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem', color: '#212529', fontWeight: 600 }}>
                      {branch.name}
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem' }}>
                      <div>
                        <strong>{t('restaurantDetails.representative')}:</strong> {branch.representativeName}
                      </div>
                      <div>
                        <strong>{t('common.phone')}:</strong> {branch.phone}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>
                        <strong>{t('restaurantDetails.coordinates')}:</strong> {branch.latitude.toFixed(4)}, {branch.longitude.toFixed(4)}
                      </div>
                    </div>
                  </div>
                  <LocationMap
                    latitude={branch.latitude}
                    longitude={branch.longitude}
                    title={`${restaurant.name} - ${branch.name}`}
                    height="150px"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Menu */}
        <div className="detail-card full-width">
          <h3>{t('restaurantDetails.menu')}</h3>
          {categoriesWithFoods.length === 0 ? (
            <div className="no-data">{t('restaurantDetails.noMenuItems')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {categoriesWithFoods.map((category, index) => (
                <div key={index}>
                  <h4 style={{
                    margin: '0 0 0.75rem 0',
                    color: '#495057',
                    fontSize: '1rem',
                    borderBottom: '2px solid #28a745',
                    paddingBottom: '0.4rem'
                  }}>
                    {category.categoryName} ({category.foods.length})
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '1rem'
                  }}>
                    {category.foods.map((food, foodIndex) => (
                      <div key={food.id} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#fff',
                        transition: 'box-shadow 0.2s',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        <button
                          onClick={() => openEditFoodModal(food, category.categoryId)}
                          style={{
                            position: 'absolute',
                            top: '0.5rem',
                            right: '0.5rem',
                            backgroundColor: '#007bff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.4rem 0.6rem',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            zIndex: 10,
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#0056b3';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#007bff';
                          }}
                        >
                          {t('restaurantDetails.editFood')}
                        </button>
                        {food.foodImage && (
                          <div
                            style={{
                              width: '100%',
                              height: '180px',
                              overflow: 'hidden',
                              backgroundColor: '#f8f9fa',
                              position: 'relative',
                              cursor: 'pointer'
                            }}
                            onClick={() => setViewerImage({
                              url: imageIdToUrl(food.foodImage) || '',
                              title: `${restaurant.name} - ${food.name}`
                            })}
                          >
                            <img
                              src={imageIdToUrl(food.foodImage) || ''}
                              alt={food.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                            {!food.isAvailable && (
                              <div style={{
                                position: 'absolute',
                                top: '0.5rem',
                                left: '0.5rem',
                                backgroundColor: '#dc3545',
                                color: '#fff',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}>
                                {t('restaurantDetails.unavailable')}
                              </div>
                            )}
                          </div>
                        )}
                        <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
                          <div style={{
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            color: '#212529',
                            lineHeight: '1.3'
                          }}>
                            {food.name}
                          </div>
                          <div style={{
                            fontSize: '0.8rem',
                            color: '#6c757d',
                            lineHeight: '1.4',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {food.description}
                          </div>
                          <div style={{
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            color: '#28a745',
                            marginTop: 'auto'
                          }}>
                            {formatPrice(food.price)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ChangeRestaurantStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onSubmit={handleChangeStatus}
        currentStatus={restaurant.applicationStatus}
        loading={modalLoading}
      />

      <ChangeRestaurantBalanceModal
        isOpen={isBalanceModalOpen}
        onClose={() => setIsBalanceModalOpen(false)}
        onSubmit={handleChangeBalance}
        currentBalance={restaurant.balance}
        loading={modalLoading}
      />

      <RestaurantCommissionModal
        isOpen={isCommissionModalOpen}
        onClose={() => setIsCommissionModalOpen(false)}
        onSubmit={handleConfigureCommission}
        onDelete={handleDeleteCommission}
        currentConfig={commissionConfig}
        globalDefaultPercentage={globalDefaultCommission}
        loading={modalLoading}
        deleting={commissionDeleting}
      />

      <ImageViewerModal
        isOpen={!!viewerImage}
        onClose={() => setViewerImage(null)}
        imageUrl={viewerImage?.url || null}
        imageTitle={viewerImage?.title}
      />

      <RestaurantStatisticsModal
        isOpen={isStatisticsModalOpen}
        onClose={() => setIsStatisticsModalOpen(false)}
        restaurantId={restaurantId || ''}
        restaurantName={restaurant.name}
      />

      <EditRestaurantModal
        isOpen={isEditRestaurantModalOpen}
        onClose={() => setIsEditRestaurantModalOpen(false)}
        onSubmit={handleEditRestaurant}
        restaurant={restaurant}
        loading={modalLoading}
      />

      {selectedFood && (
        <EditFoodModal
          isOpen={isEditFoodModalOpen}
          onClose={() => {
            setIsEditFoodModalOpen(false);
            setSelectedFood(null);
          }}
          onSubmit={handleEditFood}
          food={selectedFood}
          categories={categoriesWithFoods}
          loading={modalLoading}
        />
      )}

      {restaurantId && (
        <ManageRestaurantCategoriesModal
          isOpen={isManageCategoriesModalOpen}
          onClose={() => setIsManageCategoriesModalOpen(false)}
          restaurantId={parseInt(restaurantId)}
          onCategoriesUpdated={() => fetchRestaurantDetails(parseInt(restaurantId))}
        />
      )}
    </div>
  );
};

export default RestaurantDetails;
